import { CanActivateFn, Params } from '@angular/router';
import { inject } from '@angular/core';
import { PublicBoxAuthService } from '@web/features/shop';
import {
  isForbidden,
  Route,
  RouteParam,
  UrlTreeProviderService,
} from '@web/common';
import { isNil, isServerContext } from '@web/utils';
import { catchError, map, of, switchMap, throwError } from 'rxjs';

const getBoxesUrlTree = (provider: UrlTreeProviderService) => {
  return provider.urlTree((provider) => provider.routerLink(Route.PL_Boxes));
};

const getDetailsUrlTree = (
  provider: UrlTreeProviderService,
  slug: string,
  queryParams: Params,
) => {
  return provider.urlTree(
    (provider) =>
      provider.routerLink(Route.PL_BoxDetails, {
        [RouteParam.Slug]: slug,
      }),
    {
      queryParams,
      queryParamsHandling: 'merge',
    },
  );
};

export const PublicBoxAuthorizationGuard: CanActivateFn = (route) => {
  const publicBoxAuth = inject(PublicBoxAuthService);
  const isServer = isServerContext();
  const slug = route.paramMap.get(RouteParam.Slug);
  const provider = inject(UrlTreeProviderService);
  const queryParams = route.queryParams;

  // Po stronie serwera robię tymczasowe przekierowanie na stronę ładowania.
  if (isServer) {
    return false;
  }

  if (isNil(slug)) {
    return getBoxesUrlTree(provider);
  }

  return publicBoxAuth.isAuthorizationRequired(slug).pipe(
    switchMap((result) => {
      if (result) {
        return publicBoxAuth.fetchAuthorizedDetails(slug).pipe(
          map(() => getDetailsUrlTree(provider, slug, queryParams)),
          catchError((response) => {
            if (isForbidden(response)) {
              return of(true);
            }

            return throwError(() => response);
          }),
        );
      }

      return of(getDetailsUrlTree(provider, slug, queryParams));
    }),
  );
};
